import {useEffect, useRef, useState, type FormEvent} from 'react';
import {ApiError} from '../api/client';
import {
  useDismissItem,
  useLinkImportItems,
  usePreviewLink,
  useRetryItem,
  useStartImport,
  type LinkImportItem,
  type LinkImportPreview,
  type LinkImportResource,
  type LinkImportSkipReason,
  type ShownLinkPreview,
} from '../api/linkImports';

interface Props {
  resource: LinkImportResource;
  requestId: string;
  token: string;
  // Идёт перенос (queued/running): странице нужно знать, чтобы закрыть сдачу.
  onActiveChange: (active: boolean) => void;
  // Строка стала `done`: в папке запроса появился файл, список файлов страницы устарел.
  onFilesChanged: () => void;
}

// Ссылка → предпросмотр → «Перенести» → строки с живым ходом. Блок сам опрашивает
// сервер; странице отдаёт только два сигнала (см. Props).
export function DriveLinkImport({resource, requestId, token, onActiveChange, onFilesChanged}: Props) {
  const list = useLinkImportItems(resource, requestId, token);
  const previewLink = usePreviewLink(resource, requestId, token);
  const startImport = useStartImport(resource, requestId, token);
  const retryItem = useRetryItem(resource, requestId, token);
  const dismissItem = useDismissItem(resource, requestId, token);

  const [text, setText] = useState('');
  // Показанный предпросмотр (с ключом идемпотентности); null — нечего переносить.
  const [shown, setShown] = useState<ShownLinkPreview | null>(null);

  // Пока POST переноса летит, он уже считается идущим: иначе между нажатием и
  // первым ответом сдача на мгновение открыта.
  const active = (list.data?.active ?? false) || startImport.isPending;
  const items = list.data?.items ?? [];

  // Колбэки страницы читаем через ref: нестабильная стрелка в props не должна
  // перезапускать эффекты ниже.
  const onActiveChangeRef = useRef(onActiveChange);
  const onFilesChangedRef = useRef(onFilesChanged);
  useEffect(() => {
    onActiveChangeRef.current = onActiveChange;
    onFilesChangedRef.current = onFilesChanged;
  });

  useEffect(() => {
    onActiveChangeRef.current(active);
  }, [active]);
  // Блок ушёл с экрана (запрос сдан) — запрет сдачи с него снимается.
  useEffect(() => () => onActiveChangeRef.current(false), []);

  // Новые `done` и строки, что пропали из списка. Последняя строка переноса
  // уходит из списка вместе с остальными `done` (сервер показывает их, лишь пока
  // в переносе есть queued/running), поэтому один опрос может не застать её в
  // статусе `done`: исчезнувшая активная строка — тоже готовый файл.
  const reportedDone = useRef(new Set<string>());
  const previousActive = useRef(new Set<string>());
  const data = list.data;
  useEffect(() => {
    if (!data) return;
    let changed = false;
    const currentIds = new Set<string>();
    const currentActive = new Set<string>();
    for (const item of data.items) {
      currentIds.add(item.id);
      if (item.status === 'queued' || item.status === 'running') currentActive.add(item.id);
      if (item.status === 'done' && !reportedDone.current.has(item.id)) {
        reportedDone.current.add(item.id);
        changed = true;
      }
    }
    for (const id of previousActive.current) {
      if (!currentIds.has(id)) changed = true;
    }
    previousActive.current = currentActive;
    if (changed) onFilesChangedRef.current();
  }, [data]);

  function onTextChange(value: string) {
    setText(value);
    // Предпросмотр относится к прежней ссылке.
    setShown(null);
    previewLink.reset();
    startImport.reset();
  }

  async function onPaste() {
    try {
      const clipboardText = await navigator.clipboard.readText();
      if (clipboardText) onTextChange(clipboardText.trim());
    } catch {
      // Отказ в доступе к буферу — молча: ссылку можно вставить в поле руками.
    }
  }

  function onShow(e: FormEvent) {
    e.preventDefault();
    const url = text.trim();
    if (!url || previewLink.isPending) return;
    startImport.reset();
    setShown(null);
    previewLink.mutate(url, {onSuccess: setShown});
  }

  function onStart() {
    if (!shown) return;
    startImport.mutate(
      {url: shown.url, idempotencyKey: shown.idempotencyKey},
      {
        onSuccess: () => {
          setText('');
          setShown(null);
        },
        onError: (err) => {
          // Отказ по существу (ссылка/доступ/пусто/запрос сдан) делает предпросмотр
          // негодным. Обрыв связи и сбой сервиса — нет: «Перенести» ещё раз, с тем же ключом.
          if (!isTransientError(err)) setShown(null);
        },
      },
    );
  }

  const canPaste = typeof navigator !== 'undefined' && typeof navigator.clipboard?.readText === 'function';
  const requestError = startImport.error ?? previewLink.error;
  const actionError = [retryItem.error, dismissItem.error].find(isShownActionError);

  return (
    <section className="space-y-3 border border-slate-200 rounded-2xl bg-white p-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">Ссылка на Google Диск</h2>
        <p className="text-xs text-slate-500">
          В Google Диске: Поделиться → Доступ: «Все, у кого есть ссылка» → Копировать ссылку
        </p>
      </div>

      <form onSubmit={onShow} className="space-y-2">
        <input
          type="text"
          inputMode="url"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder="Вставьте ссылку на файл или папку"
          aria-label="Ссылка на Google Диск"
          className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base sm:text-sm placeholder:text-slate-400 focus:outline-none focus:border-slate-500"
        />
        <div className="flex flex-wrap gap-2">
          {canPaste && (
            <button
              type="button"
              onClick={() => void onPaste()}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Вставить
            </button>
          )}
          <button
            type="submit"
            disabled={text.trim() === '' || previewLink.isPending}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {previewLink.isPending ? 'Проверяем…' : 'Показать'}
          </button>
        </div>
      </form>

      {requestError != null && (
        <p className="text-sm text-red-600">{describeLinkImportError(errorCode(requestError))}</p>
      )}

      {shown && (
        <PreviewSummary
          preview={shown.preview}
          pending={startImport.isPending}
          onStart={onStart}
        />
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-slate-200 border border-slate-200 rounded-xl overflow-hidden">
          {items.map((item) => (
            <ImportRow
              key={item.id}
              item={item}
              retrying={retryItem.isPending && retryItem.variables === item.id}
              dismissing={dismissItem.isPending && dismissItem.variables === item.id}
              onRetry={() => retryItem.mutate(item.id)}
              onDismiss={() => dismissItem.mutate(item.id)}
            />
          ))}
        </ul>
      )}
      {actionError != null && (
        <p className="text-sm text-red-600">{describeLinkImportError(errorCode(actionError))}</p>
      )}
    </section>
  );
}

function PreviewSummary({
  preview,
  pending,
  onStart,
}: {
  preview: LinkImportPreview;
  pending: boolean;
  onStart: () => void;
}) {
  const alreadyCount = preview.items.filter((item) => item.already).length;
  const reasons = skipReasonsLabel(preview.skipped.map((s) => s.reason));
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2">
      <div className="space-y-0.5 text-sm">
        <p className="font-medium text-slate-900">
          Найдено файлов: {preview.totals.count}, {formatBytes(preview.totals.bytes)}
        </p>
        {preview.skipped.length > 0 && (
          <p className="text-slate-600">
            Пропущено: {preview.skipped.length}
            {reasons && ` (${reasons})`}
          </p>
        )}
        {alreadyCount > 0 && <p className="text-slate-600">Уже перенесено: {alreadyCount}</p>}
      </div>
      <button
        type="button"
        onClick={onStart}
        disabled={pending}
        className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {pending ? 'Переносим…' : 'Перенести'}
      </button>
    </div>
  );
}

function ImportRow({
  item,
  retrying,
  dismissing,
  onRetry,
  onDismiss,
}: {
  item: LinkImportItem;
  retrying: boolean;
  dismissing: boolean;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const failed = item.status === 'failed';
  const toneClass = failed ? 'text-red-600' : item.status === 'done' ? 'text-emerald-700' : 'text-slate-600';
  return (
    <li className="p-3 space-y-1.5 bg-white">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 break-all text-sm font-medium text-slate-900">{item.name}</p>
        <span className="shrink-0 text-xs text-slate-500 tabular-nums pt-0.5">{formatBytes(item.sizeBytes)}</span>
      </div>
      <p className={`text-xs ${toneClass}`}>{itemStatusLabel(item)}</p>
      {item.status === 'running' && item.progress !== null && (
        <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-sky-500 transition-[width]" style={{width: `${item.progress}%`}} />
        </div>
      )}
      {failed && (
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying || dismissing}
            className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Повторить
          </button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={retrying || dismissing}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Убрать
          </button>
        </div>
      )}
    </li>
  );
}

function withProgress(label: string, progress: number | null): string {
  return progress === null ? label : `${label} · ${progress}%`;
}

function itemStatusLabel(item: LinkImportItem): string {
  if (item.status === 'failed') return `Ошибка: ${describeLinkImportError(item.error)}`;
  if (item.status === 'done') {
    return item.outcome === 'original' ? 'Готово · оригинал (сжать не вышло)' : 'Готово';
  }
  switch (item.phase) {
    case 'waiting_disk':
      return 'Ждём место на сервере';
    case 'copying':
      return 'Копируем';
    case 'downloading':
      return withProgress('Скачиваем', item.progress);
    case 'compressing':
      return withProgress('Сжимаем', item.progress);
    case 'uploading':
      return withProgress('Загружаем', item.progress);
    default:
      return 'В очереди';
  }
}

// Порядок причин в строке «Пропущено» — по важности для модели, не по порядку файлов.
const SKIP_REASON_LABELS: ReadonlyArray<readonly [LinkImportSkipReason, string]> = [
  ['subfolder', 'вложенные папки'],
  ['shortcut', 'ярлыки'],
  ['google_doc', 'документы Google'],
  ['not_media', 'не фото и не видео'],
  ['trashed', 'в корзине'],
  ['download_disabled', 'скачивание запрещено'],
];

// Только причины, что встретились в этой ссылке.
function skipReasonsLabel(reasons: LinkImportSkipReason[]): string {
  const met = new Set(reasons);
  return SKIP_REASON_LABELS.filter(([reason]) => met.has(reason))
    .map(([, label]) => label)
    .join(', ');
}

const UNAVAILABLE_TEXT = 'Сервис загрузки временно недоступен';

// Коды `error` предпросмотра/переноса и ошибок строк. quota, drive, internal,
// drive_disconnected, сеть и всё незнакомое — один текст «сервис недоступен».
const ERROR_TEXTS: Record<string, string> = {
  bad_link: 'Это не ссылка на Google Диск',
  no_access: 'Нет доступа. Откройте доступ «Все, у кого есть ссылка» и вставьте ссылку ещё раз',
  download_disabled:
    'В настройках файла запрещено скачивание — разрешите его или загрузите файл с телефона',
  empty: 'В папке нет новых фото и видео для переноса',
  too_many_files: 'В папке больше 200 файлов — разделите её на части',
  source_gone: 'Файл удалён или доступ закрыт',
  not_uploading_state: 'Запрос уже отправлен',
};

function describeLinkImportError(code: string | null | undefined): string {
  return code && Object.hasOwn(ERROR_TEXTS, code) ? (ERROR_TEXTS[code] ?? UNAVAILABLE_TEXT) : UNAVAILABLE_TEXT;
}

function errorCode(err: unknown): string | null {
  return err instanceof ApiError ? err.code : null;
}

// Обрыв связи (0) и сбой сервера (5xx) — повторить то же действие имеет смысл.
function isTransientError(err: unknown): boolean {
  return !(err instanceof ApiError) || err.status === 0 || err.status >= 500;
}

// «Повторить»/«Убрать» по строке, которую уже поменяли (сервер ответил not_failed,
// item_active, item_not_found), — не ошибка для модели: список перечитывается и
// показывает, как есть.
const STALE_ROW_ERRORS = new Set(['not_failed', 'item_active', 'item_not_found']);

function isShownActionError(err: unknown): boolean {
  if (err == null) return false;
  const code = errorCode(err);
  return code === null || !STALE_ROW_ERRORS.has(code);
}

// Вопрос в окне сдачи, когда есть неубранные ошибки переноса: «3 файла не
// перенеслись. Всё равно отправить?» (число и падеж — как говорят по-русски).
export function failedImportsQuestion(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  if (last === 1 && lastTwo !== 11) return `${count} файл не перенёсся. Всё равно отправить?`;
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) {
    return `${count} файла не перенеслись. Всё равно отправить?`;
  }
  return `${count} файлов не перенеслись. Всё равно отправить?`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}
